import type { ReactNode } from 'react';

import { exampleUrl } from '../lib/useExamples';

interface Props {
  title: string;
  description?: string;
  /** Small technical line — lens, posture, ratio. */
  spec?: string;
  selected: boolean;
  onToggle: () => void;
  /** Generated example photograph, when one exists. Resolved under /examples. */
  example?: string;
  /** A ready URL to show as-is, for media the server renders on demand. */
  image?: string;
  /** Drawn when there is no photograph yet. */
  fallback?: ReactNode;
  /** Flat colour stand-in for scenes, behind the fallback. */
  swatch?: string;
  /** Position in a cycle, for pickers where order matters. */
  order?: number;
  /** Photographs are 2:3; light and grade samples are 4:5. */
  ratio?: '2:3' | '4:5';
  /**
   * Off where the picture says it all — a pose, a place: the name is still
   * the card's accessible name and its tooltip, just not written over it.
   */
  caption?: boolean;
}

/**
 * One choice, shown as a picture.
 *
 * Every picker in the shoot builder uses this, so a pose, an angle, a location
 * and a colour grade are all chosen the same way — look at it, click it. The
 * image is deliberately large: a 54px thumbnail cannot tell you whether a pose
 * is worth shooting.
 */
export function OptionCard({
  title,
  description,
  spec,
  selected,
  onToggle,
  example,
  image,
  fallback,
  swatch,
  order,
  ratio = '2:3',
  caption = true,
}: Props) {
  return (
    <button
      type="button"
      className={`opt ${selected ? 'is-on' : ''}`}
      aria-pressed={selected}
      // The description is the tooltip now that the caption is compact.
      title={description ? `${title} — ${description}` : title}
      aria-label={caption ? undefined : title}
      onClick={onToggle}
    >
      <span className={`opt__media opt__media--${ratio === '2:3' ? '23' : '45'}`}>
        {(image ?? example) ? (
          <img src={image ?? exampleUrl(example!)} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="opt__fallback" style={swatch ? { background: swatch } : undefined}>
            {fallback}
          </span>
        )}
        {order !== undefined && <span className="opt__order">{order}</span>}
        {selected && <span className="opt__tick">✓</span>}

        {/* Overlaid rather than stacked underneath: in a rail, a text block
            under every card doubles the height of the whole row. */}
        {caption && (
          <span className="opt__caption">
            <span className="opt__title">{title}</span>
            {spec && <span className="opt__spec">{spec}</span>}
          </span>
        )}
      </span>
    </button>
  );
}
